/**
 * Bidirectional postMessage bridge: parent fixed proxy toolbar ↔ iframe form actions.
 * Uses dedicated CSS classes (not .site-actions-container / .site-action) to avoid parent layout rules.
 */

export const TRANSLATION_TOOLBAR_MESSAGE_TYPE = 'naxos:translation-toolbar';

const SAVE_SELECTOR = '#save, #save-stay, #popupsave, #popupsave-stay';
const COMMAND_SAVE = 'save';
const COMMAND_SAVE_STAY = 'save-stay';
const COMMAND_CLOSE = 'close';
const COMMAND_PREVIEW = 'preview';
const COMMAND_DELETE = 'delete';
const COMMAND_DELETE_CONFIRMED = 'delete-confirmed';
const COMMAND_PUSH = 'push';

/**
 * @param {unknown} data
 * @returns {boolean}
 */
const isTranslationToolbarMessage = (data) => {
	return typeof data === 'object' && data !== null && data.type === TRANSLATION_TOOLBAR_MESSAGE_TYPE;
};

/**
 * @param {string} [target_origin]
 * @returns {string}
 */
const resolveTargetOrigin = (target_origin) => {
	if (target_origin) {
		return target_origin;
	}

	return window.location.origin;
};

/**
 * Delete must be shown only for persisted translations (id exists).
 *
 * @returns {boolean}
 */
const translationHasPersistedId = () => {
	const raw_id = $('#id').val() ?? $('input[name="id"]').val();

	if (raw_id === undefined || raw_id === null) {
		return false;
	}

	const normalized_id = String(raw_id).trim();

	return normalized_id.length > 0 && normalized_id !== '0';
};

/**
 * @returns {import('./translation-toolbar-bridge').TranslationToolbarState}
 */
export function collectTranslationToolbarState() {
	const $save_buttons = $(SAVE_SELECTOR);
	const is_translation_popup = document.body.classList.contains('popup-translation');
	// In popup-translation the real toolbar is hidden (proxy on parent); :visible would always be false.
	const $save_buttons_for_enable = is_translation_popup ? $save_buttons : $save_buttons.filter(':visible');
	const first_save_button = $save_buttons_for_enable.first();
	const saving = $save_buttons.filter('.submitting').length > 0;
	const readonly = !!window.forceReadonly;
	const has_save_stay = $('#save-stay, #popupsave-stay').length > 0;
	const has_save_close = $('#save, #popupsave').length > 0;

	let save_enabled = false;

	if (!readonly && first_save_button.length) {
		save_enabled = !first_save_button.prop('disabled') && !first_save_button.hasClass('disabled');
	}

	return {
		readonly,
		saveEnabled: save_enabled,
		saving,
		buttons: {
			preview: $('[data-tag="preview"]').length > 0,
			push: $('#push-button').length > 0,
			close: $('#close').length > 0,
			delete: translationHasPersistedId() && $('[data-tag="delete"]').length > 0,
			save: has_save_close && !readonly,
			saveStay: has_save_stay && !readonly,
		},
	};
}

/**
 * @param {string} command
 */
export function executeTranslationToolbarCommand(command) {
	const triggerExisting = (selector, { enabledOnly = false } = {}) => {
		let $target = $(selector);

		if (enabledOnly) {
			$target = $target.filter(':not(:disabled)').filter((_idx, element) => !$(element).hasClass('disabled'));
		}

		$target.first().trigger('click');
	};

	switch (command) {
		case COMMAND_PREVIEW:
			if (typeof window.showPreview === 'function') {
				window.showPreview();
			} else {
				triggerExisting('[data-tag="preview"]');
			}
			break;
		case COMMAND_PUSH:
			triggerExisting('#push-button');
			break;
		case COMMAND_CLOSE:
			triggerExisting('#close');
			break;
		case COMMAND_DELETE:
			triggerExisting('[data-tag="delete"]');
			break;
		case COMMAND_DELETE_CONFIRMED:
			window.naxosTranslationToolbarDeleteConfirmed = true;
			triggerExisting('[data-tag="delete"]');
			break;
		case COMMAND_SAVE:
			triggerExisting('#save, #popupsave', { enabledOnly: true });
			break;
		case COMMAND_SAVE_STAY:
			triggerExisting('#save-stay, #popupsave-stay', { enabledOnly: true });
			break;
		default:
			break;
	}
}

/**
 * Iframe document: publish toolbar state to parent and handle commands.
 */
export function initTranslationToolbarBridgeChild() {
	if (!document.body.classList.contains('popup-translation') || window.self === window.top) {
		return;
	}

	let publish_timeout = null;
	const target_origin = resolveTargetOrigin();

	const publishState = () => {
		if (!window.parent || window.parent === window) {
			return;
		}

		window.parent.postMessage(
			{
				type: TRANSLATION_TOOLBAR_MESSAGE_TYPE,
				action: 'state',
				state: collectTranslationToolbarState(),
			},
			target_origin,
		);
	};

	const schedulePublishState = (immediate = false) => {
		if (immediate) {
			if (publish_timeout) {
				clearTimeout(publish_timeout);
				publish_timeout = null;
			}

			publishState();

			return;
		}

		if (publish_timeout) {
			clearTimeout(publish_timeout);
		}

		publish_timeout = setTimeout(() => {
			publish_timeout = null;
			publishState();
		}, 80);
	};

	window.naxosTranslationToolbarBridge = {
		publishState,
		schedulePublishState,
	};

	window.addEventListener('message', (event) => {
		if (event.origin !== window.location.origin) {
			return;
		}

		if (!isTranslationToolbarMessage(event.data)) {
			return;
		}

		if (event.data.action === 'command' && typeof event.data.command === 'string') {
			executeTranslationToolbarCommand(event.data.command);
			schedulePublishState(true);

			return;
		}

		if (event.data.action === 'request-state') {
			publishState();
		}
	});

	$(document).on(
		'naxos:content-async-saved.naxosTranslationToolbar naxos:content-async-save-failed.naxosTranslationToolbar',
		schedulePublishState,
	);

	$('#pageForm').on('change.naxosTranslationToolbar', schedulePublishState);
	$('#pageForm').on('input.naxosTranslationToolbar', schedulePublishState);

	const observer_target = document.getElementById('pageForm');
	if (observer_target && typeof MutationObserver !== 'undefined') {
		const state_observer = new MutationObserver(() => {
			schedulePublishState();
		});

		state_observer.observe(observer_target, {
			attributes: true,
			attributeFilter: ['disabled', 'class', 'style', 'value'],
			subtree: true,
			childList: true,
		});
	}

	window.addEventListener('handshakeDone', () => {
		schedulePublishState();
	});

	$(() => {
		schedulePublishState();
		setTimeout(schedulePublishState, 400);
		setTimeout(schedulePublishState, 1200);
	});
}

/**
 * @typedef {object} TranslationToolbarState
 * @property {boolean} readonly
 * @property {boolean} saveEnabled
 * @property {boolean} saving
 * @property {object} buttons
 * @property {boolean} buttons.preview
 * @property {boolean} buttons.push
 * @property {boolean} buttons.close
 * @property {boolean} buttons.delete
 * @property {boolean} buttons.save
 * @property {boolean} buttons.saveStay
 */

/**
 * Parent page: fixed proxy toolbar wired to translation iframe.
 *
 * @param {object} options
 * @param {JQuery} options.$iframe
 * @param {() => boolean} [options.isPanelVisible]
 * @param {() => void} [options.onDeleteConfirmed] Called right after the delete command is sent, so the parent can collapse the panel.
 */
export function initTranslationToolbarProxyParent(options) {
	const $iframe = options.$iframe;
	const is_panel_visible =
		typeof options.isPanelVisible === 'function' ? options.isPanelVisible : () => true;
	const on_delete_confirmed =
		typeof options.onDeleteConfirmed === 'function' ? options.onDeleteConfirmed : null;

	const $proxy = $('#translation-toolbar-proxy');

	if (!$proxy.length || !$iframe.length) {
		return null;
	}

	const $buttons = $proxy.find('[data-translation-command]');

	const sendCommand = (command) => {
		const iframe_element = $iframe.get(0);

		if (!iframe_element?.contentWindow || !is_panel_visible()) {
			return;
		}

		iframe_element.contentWindow.postMessage(
			{
				type: TRANSLATION_TOOLBAR_MESSAGE_TYPE,
				action: 'command',
				command,
			},
			window.location.origin,
		);
	};

	const requestState = () => {
		const iframe_element = $iframe.get(0);

		if (!iframe_element?.contentWindow || !is_panel_visible()) {
			return;
		}

		iframe_element.contentWindow.postMessage(
			{
				type: TRANSLATION_TOOLBAR_MESSAGE_TYPE,
				action: 'request-state',
			},
			window.location.origin,
		);
	};

	const applySaveButtonLoadingStyle = ($btn, is_loading) => {
		$btn.toggleClass('submitting', is_loading);

		if (is_loading) {
			$btn.css({ cursor: 'not-allowed', opacity: '0.75' });
		} else {
			$btn.css({ cursor: '', opacity: '' });
		}
	};

	/**
	 * @param {TranslationToolbarState} state
	 */
	const applyState = (state) => {
		if (!state) {
			return;
		}

		const button_flags = state.buttons ?? {};

		$buttons.each(function () {
			const $btn = $(this);
			const command = $btn.attr('data-translation-command');

			if (!command) {
				return;
			}

			const flag_key = command === COMMAND_SAVE_STAY ? 'saveStay' : command;
			const visible = button_flags[flag_key] === true;

			if (visible) {
				$btn.removeAttr('hidden');
			} else {
				$btn.attr('hidden', 'hidden');
			}

			if (!visible) {
				return;
			}

			const is_save = command === COMMAND_SAVE || command === COMMAND_SAVE_STAY;

			if (is_save) {
				const disabled = state.readonly || !state.saveEnabled || state.saving;
				$btn.prop('disabled', disabled);
				$btn.toggleClass('translation-toolbar-proxy__btn--disabled', disabled);
				applySaveButtonLoadingStyle($btn, !!state.saving);
			} else {
				$btn.prop('disabled', !!state.readonly);
				$btn.toggleClass('translation-toolbar-proxy__btn--disabled', !!state.readonly);
				applySaveButtonLoadingStyle($btn, false);
			}
		});
	};

	const showProxy = () => {
		$proxy.removeAttr('hidden').attr('aria-hidden', 'false');
		requestState();
	};

	const hideProxy = () => {
		// Closing from a proxy button (e.g. the X) leaves its hover tooltip on screen:
		// hiding the button fires no mouseleave, so Bootstrap never tears the tooltip down.
		// Force-hide them before the toolbar disappears. ('hide' is a no-op when uninitialised.)
		$proxy.find('[data-toggle="tooltip"]').tooltip('hide');
		$proxy.attr('hidden', 'hidden').attr('aria-hidden', 'true');
	};

	$proxy.on('click.translationToolbarProxy', '[data-translation-command]', function (e) {
		e.preventDefault();

		const $btn = $(this);

		if ($btn.prop('disabled') || $btn.hasClass('translation-toolbar-proxy__btn--disabled')) {
			return;
		}

		const command = $btn.attr('data-translation-command');

		if (command === COMMAND_DELETE) {
			bootbox.confirm({
				message: 'Confermare eliminazione della traduzione? L\'operazione non sarà annullabile.',
				buttons: {
					cancel: {
						label: 'Annulla',
						className: 'btn-default',
					},
					confirm: {
						label: 'Elimina traduzione',
						className: 'btn-danger',
					},
				},
				callback: (confirmed) => {
					if (confirmed) {
						// Send the delete first (panel still expanded so sendCommand's visibility
						// gate passes), then let the parent collapse the panel so the user never
						// sees the iframe navigate to the post-delete listing page.
						sendCommand(COMMAND_DELETE_CONFIRMED);

						if (on_delete_confirmed) {
							on_delete_confirmed();
						}
					}
				},
			});
			return;
		}

		if (command === COMMAND_SAVE || command === COMMAND_SAVE_STAY) {
			// Let the iframe set `saving` via the bridge after validation — optimistic loading here
			// could stick if the POST never starts (sync categorization forms + programmatic click).
			sendCommand(command);
			requestState();
			setTimeout(requestState, 150);
			setTimeout(requestState, 400);

			return;
		}

		if (command) {
			sendCommand(command);
		}
	});

	const controller = {
		show: showProxy,
		hide: hideProxy,
		applyState,
		requestState,
	};

	return controller;
}

/**
 * Parent: handle iframe → parent state messages (register alongside translations.js listener).
 *
 * @param {MessageEvent} event
 * @param {HTMLIFrameElement|null} iframe_element
 * @param {{ applyState: (state: TranslationToolbarState) => void }|null} controller
 * @returns {boolean} true if handled
 */
export function handleTranslationToolbarMessageFromIframe(event, iframe_element, controller) {
	if (!controller || !iframe_element?.contentWindow || event.source !== iframe_element.contentWindow) {
		return false;
	}

	if (!isTranslationToolbarMessage(event.data) || event.data.action !== 'state') {
		return false;
	}

	controller.applyState(event.data.state);

	return true;
}
